<template>
    <div
        id="lt-yantr"
        class="debt-repayment">
        <default-calculator-layout
            :filter-width="filterWidth"
            :output-width="outputWidth"
            :calculator-title="calculatorTitle">
            <div slot="filter">
                <number-input
                    v-model="LoanBalance"
                    :label="labelLoanBalance"
                    :value="loanBalance"
                    inputmode="decimal"
                />
                <number-input
                    v-model="InterestRate"
                    :label="labelInterestRate"
                    symbol="%"
                    symbol-aligned="right"
                    precision="3"
                    :value="interestRateToken"
                    max-value="100.000"
                    inputmode="decimal"
                />
                <number-input
                    v-model="MonthlyPayment"
                    :label="labelMonthlyPayment"
                    precision="2"
                    inputmode="decimal"
                />
                <label>OR</label>
                <number-input
                    v-model="LoanTermInMonths"
                    :label="labelPayoffTimeframe"
                    symbol="mos."
                    symbol-aligned="right"
                    inputmode="decimal"
                />
            </div>
            <div
                slot="output"
                class="container-fluid">
                <div class="loan-comparison">
                    <table class="table table-border-none">
                        <tbody>
                            <tr>
                                <td class="big-total-heading">
                                    {{ displayTopTitle }}
                                </td>
                                <td
                                    v-if="!!LoanTermInMonths"
                                    class="big-total text-right green-text">
                                    <span v-if="!showErrorMessage">{{ (displayMonthlyPayment()) }}</span>
                                </td>
                                <td
                                    v-else-if="!LoanTermInMonths"
                                    class="big-total text-right green-text">
                                    <span v-if="!showErrorMessage">{{ displayLoanTermInMonths }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div
                    v-if="!showErrorMessage"
                    class="analysis-results debt-consolidation-results">
                    <table class="table table-border-none">
                        <tr>
                            <td class="small-total-heading text-left">
                                {{ displayBottomTitle }}
                            </td>
                            <td
                                v-if="!!LoanTermInMonths"
                                class=" small-total text-right green-text">
                                {{ displayLoanTermInMonths }}
                            </td>
                            <td
                                v-else
                                class=" small-total text-right green-text">
                                {{ displayMonthlyPayment() }}
                            </td>
                        </tr>
                    </table>
                </div>
                <div class="analysis-results debt-consolidation-results loan-comparison-error">
                    {{ showErrorMessage }}
                </div>
                <div class="text-center">
                    <button
                        class="btn btn-blue mt-none"
                        type="submit"
                        @click="redirect()">
                        {{ DestinationButtonText }}
                    </button>
                </div>
                <div class="disclosures">
                    <advertising-disclosures
                        v-if="DisclosureText"
                        :disclosure-pre-text="disclosurePreText"
                        :disclosure-text="DisclosureText"
                        :disclosure-tokens="DisclosureTokens"
                    />
                </div>
                <lt-logo v-if="ltLogo==1">
                    <small>NMLS#1136 Terms & Conditions Apply</small>
                </lt-logo>
            </div>
        </default-calculator-layout>
    </div>
</template>

<script>
    import Accounting from 'accounting';
    import PaymentHelper from '~/helpers/payment';
    import BaseMixin from '~/mixin/base-mixin';
    import LtLogo from '~/components/lt-logo.vue';
    import DefaultCalculatorLayout from '~/layout/default-calculator.vue';
    import CommonLayoutProps from '~/mixin/common-layout-props';

    export default {
        components: {
            LtLogo,
            DefaultCalculatorLayout,
        },
        mixins: [BaseMixin, CommonLayoutProps],
        props: {
            interestRateToken: {
                default: 'CC_DebtRepaymentCalc_DefaultRate',
            },
            labelLoanBalance: {
                default: 'Balance:',
            },
            labelMonthlyPayment: {
                default: 'Monthly Payment:',
            },
            labelInterestRate: {
                default: 'Interest Rate:',
            },
            labelPayoffTimeframe: {
                default: 'Payoff Timeframe:',
            },
            loanBalance: {
                default: 9000,
            },
            monthlyPayment: {
                default: 125,
            },
            interestRate: {
                default: 8,
            },
            loanTermInMonths: {
                type: Number,
                default: 0,
            },
            disclosureTokens: {
                default: '94',
            },
            disclosureText: {
                default: '',
            },
            disclosurePreText: {
                default: '',
            },
            destinationLinkOrId: {
                default: 'wp-cc-debt-repayment',
            },
            destinationButtonText: {
                default: 'Find Your Best Terms',
            },
            ltLogo: {
                default: 0,
            },
        },
        data() {
            return {
                InterestRateToken: this.interestRateToken,
                LoanBalance: this.loanBalance,
                MonthlyPayment: this.monthlyPayment,
                InterestRate: this.interestRate,
                LoanTermInMonths: this.loanTermInMonths,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
            };
        },
        computed: {
            newLoanTermInMonths() {
                if (this.MonthlyPayment > 0) {
                    this.setLoanTermInMonthsToZero();
                }
                return PaymentHelper.getMonthsLeft(this.unformatFigure(this.InterestRate), this.unformatFigure(this.LoanBalance), this.unformatFigure(this.MonthlyPayment));
            },
            newMonthlyPayment() {
                if (this.LoanTermInMonths > 0) {
                    this.setMonthlyPaymentToZero();
                }
                return PaymentHelper.getPayment(this.unformatFigure(this.LoanBalance), this.unformatFigure(this.LoanTermInMonths), PaymentHelper.convertInterestRatePerMonths(this.unformatFigure(this.InterestRate)));
            },
            displayLoanTermInMonths() {
                const calcFinalDisplayValue = this.newLoanTermInMonths;
                if (this.LoanTermInMonths && !this.MonthlyPayment) {
                    return `${this.LoanTermInMonths} months`;
                }
                if ((!this.LoanTermInMonths && !this.MonthlyPayment) || this.minPayment > this.MonthlyPayment) {
                    return '';
                }
                if (this.newLoanTermInMonths >= 240) {
                    return '';
                }
                return `${calcFinalDisplayValue} months`;
            },
            minPayment() {
                return PaymentHelper.minimumPayment(this.unformatFigure(this.LoanBalance), this.unformatFigure(this.InterestRate));
            },
            displayTopTitle() {
                if (!this.LoanTermInMonths) {
                    return 'You would pay off your debt in:';
                }
                return 'Your monthly payment would be:';
            },
            displayBottomTitle() {
                if (this.LoanTermInMonths) {
                    return 'You would pay off your debt in:';
                }
                return 'Your monthly payment would be:';
            },
            showErrorMessage() {
                if (!this.LoanTermInMonths && !this.MonthlyPayment) {
                    return 'Please enter either a payment amount or desired payoff timeframe.';
                }
                if (!!this.LoanTermInMonths && this.LoanTermInMonths >= 420) {
                    return 'The amounts you have entered exceed the maximum allowable payoff time';
                }
                if (!this.LoanTermInMonths && this.minPayment > this.MonthlyPayment) {
                    return 'Your monthly payment amount is less than the monthly interest charged by this loan.';
                }
                if (!this.LoanTermInMonths && this.newLoanTermInMonths >= 240) {
                    return 'The amounts you have entered exceed the maximum allowable payoff time';
                }
                return '';
            },
        },
        methods: {
            setLoanTermInMonthsToZero() {
                this.LoanTermInMonths = 0;
                this.displayMonthlyPayment();
            },
            setMonthlyPaymentToZero() {
                this.MonthlyPayment = 0;
            },
            displayMonthlyPayment() {
                if (this.LoanTermInMonths < 0) this.LoanTermInMonths = 0;
                const calcFinalDisplayValue = Accounting.formatMoney(this.newMonthlyPayment, { precision: 2 });
                if (this.MonthlyPayment && !this.LoanTermInMonths) {
                    return Accounting.formatMoney(this.MonthlyPayment, { precision: 2 });
                }
                if ((!this.LoanTermInMonths && !this.MonthlyPayment) || this.newMonthlyPayment === Infinity) {
                    return Accounting.formatMoney(0);
                }
                return calcFinalDisplayValue;
            },
        },
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
